import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    OnDestroy,
    OnInit,
    Output,
    PLATFORM_ID,
    ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { DfApiConfigService } from '@df/api/config/df-api-config.service';
import { HashStorageService } from '@df/browser/storage/hash-storage.service';
import { StoreFinderComponentDef } from '@df/catalog-ui/store/finder/store-finder.component.def';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import { ScrollToService } from '@df/dom/scroll/scroll-to.service';
import { IfSizeDirective } from '@df/ui/common/if-size.directive';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { Nexus } from '@tomandco/nexus-api-types';
import compact from 'lodash-es/compact';
import uniq from 'lodash-es/uniq';
import { Subscription } from 'rxjs/internal/Subscription';
import { GoogleMapComponent } from '../../../../module/google-maps/google-map.component';
import { ResultComponent } from '../../../../ui/api/result.component';
import { InputWrapBodyDirective } from '../../../../ui/form/input-wrap-body.directive';
import { InputWrapComponent } from '../../../../ui/form/input-wrap.component';
import { StoreDecorator } from '../../../model/store/store.decorator';
import { StoreDetailsComponent } from '../store-details/store-details.component';
import { StoreOpenStatusComponent } from '../store-open-status/store-open-status.component';

@Component({
    selector: 'store-finder',
    templateUrl: './store-finder.component.html',
    styleUrl: './store-finder.component.scss',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        GoogleMapComponent,
        IfSizeDirective,
        InputWrapBodyDirective,
        InputWrapComponent,
        ResultComponent,
        RouterModule,
        StoreDetailsComponent,
        StoreOpenStatusComponent,
        TranslatePipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class StoreFinderComponent extends StoreFinderComponentDef implements OnInit, OnDestroy {
    loadSeeRangeInStoreStatus = new Status();

    selectedStore?: StoreDecorator;

    countries?: Nexus.Lib.General.Country[];
    defaultCountry?: Nexus.Lib.General.Country;

    private _isMapVisible = false;

    @ViewChild('addressInput')
    addressInput?: ElementRef;

    @Output()
    storesUpdate = new EventEmitter<StoreDecorator[]>();

    private subscriptions = new Subscription();

    protected readonly isPlatformBrowser = isPlatformBrowser(inject(PLATFORM_ID));

    constructor(
        _ref: ChangeDetectorRef,
        private hashStorage: HashStorageService,
        private app: App,
        private dfApiConfigService: DfApiConfigService,
        private scrollToService: ScrollToService,
        private router: Router
    ) {
        super(_ref);
    }

    override ngOnInit() {
        this.address = this.locationState;

        const storedCountry = this.hashStorage.get('country');

        if (storedCountry) {
            this.country = storedCountry;
        } else {
            this.country = this.app.data.defaultCountry === 'IE' ? 'IE' : 'GB';
        }

        if (this.address) {
            this.search(this.initialStatus);
        } else {
            this.loadAndShowAll();
        }

        this.dfApiConfigService.loadCountries().then(() => {
            this.finderService.resource.loadAll(1).then(result => {
                // all country ids where stores are located
                const avaibleCountryIds = <string[]>compact(uniq(result.map(store => store.data?.country_id)));

                // sets countries list to show only those that have any stores
                this.countries = this.app.apiConfigService.countries
                    ?.filter(country => avaibleCountryIds.includes(country.code))
                    .sort((a, b) => (a.name < b.name ? -1 : 1));
                this.defaultCountry = this.countries?.find(country => country.code === this.app.data.defaultCountry);
                if (this.defaultCountry && this.app.data.defaultCountry !== 'IE') {
                    this.country = this.defaultCountry.code;
                    this.search();
                }
                this.detectChanges();
            });
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    get isMapVisible(): boolean {
        return this._isMapVisible;
    }

    set isMapVisible(value: boolean) {
        if (this._isMapVisible !== value) {
            this._isMapVisible = value;
            this.detectChanges();
        }
    }

    showStoreDetails(store: StoreDecorator) {
        this.selectedStore = store;

        if (this.isPlatformBrowser) {
            this.scrollToService.scrollTo(0, {
                behavior: 'smooth'
            });
        }

        this.detectChanges();
    }

    hideStoreDetails() {
        this.selectedStore = null;
        this.detectChanges();
    }

    onStoreMarkerClicked(storeId: number) {
        const store = this.findableModels?.find(store => store.id === storeId) as StoreDecorator;

        if (store.data?.url) {
            this.router.navigateByUrl(store.data.url);
        }
    }

    override onFindableModelsUpdate() {
        super.onFindableModelsUpdate();
        this.storesUpdate.emit(this.findableModels as StoreDecorator[]);
    }

    override searchByGeolocation(status = this.status, alwaysTrigger?: boolean, useStorage?: boolean): void {
        // reset country on geolocation search
        this.country = undefined;
        this.hashStorage.set('country', undefined);
        super.searchByGeolocation(status, alwaysTrigger, useStorage);
    }

    keyup(event: KeyboardEvent) {
        if (event.key === 'Enter' || event.code === 'Enter') {
            this.addressInput?.nativeElement.blur();
            this.search();
        }
    }

    get displaySeeRange(): boolean {
        return !!this.loadSeeRangeInStoreStatus.busy;
    }
}
